import React, {useEffect, useState, useMemo, useContext} from 'react';
import {Collapse} from 'antd';
import {Block} from '../block';
import {get, set, cloneDeep} from 'lodash';
import {MasterSchemaView} from '../master-schema-view';
import {MarkdownBlock} from '../markdown-block';
import {OpenApiContext} from '../../openapi-context';
import {useRouteMatch} from 'react-router-dom';
import {applyChangeSet} from './schema-block-reducer';
// Sample generation (json-schema-faker + cross-spec deref) and the
// user-authored example display were the slowest path in the editor
// and routinely froze the tab on rich foundation schemas. The tab is
// dropped for now; AI chat in the right pane will own "give me a
// sample for the open model" once that lands. Files left in tree:
//   src/components/example-editor.js
//   src/components/example-view.js
//   src/utils/deref-local-refs.js
// They have no other importers but are kept around as a starting
// point for the future Worker-driven implementation.

export function SchemaBlock({schema, className, onCancel, onSave, rootPath, mobile, header, disableEdit}) {
    const {state, dispatch} = useContext(OpenApiContext);
    const [editedSchema, setEditedSchema] = useState();

    useEffect(() => {
        if (schema) {
            setEditedSchema(cloneDeep(schema));
        }
    }, [schema]);

    // Property count drives the Properties panel label. Computed off the
    // edited copy so the title stays in sync as fields are added/removed
    // mid-edit, not just on initial mount.
    const propertyCount = useMemo(() => {
        if (!editedSchema) return 0;
        const props = editedSchema.type === 'array'
            ? get(editedSchema, 'items.properties') || {}
            : get(editedSchema, 'properties') || {};
        return Object.keys(props).length;
    }, [editedSchema]);

    function handleSave() {
        dispatch({type: 'terminate'});
        return onSave && onSave(editedSchema);
    }

    function handleCancel() {
        dispatch({type: 'terminate'});
        setEditedSchema(cloneDeep(schema));
        return onCancel && onCancel();
    }

    const handleChangeSet = (changeSet) => {
        // Functional setState. When two dispatches fire inside the same
        // browser event (e.g. an Input.onBlur-rename followed by a
        // Checkbox.onChange-flag), both previously read `editedSchema`
        // from the same stale React state, cloned it independently, and
        // the second setEditedSchema call clobbered the first — the
        // rename vanished and the row key no longer matched `editing`,
        // so the edit form collapsed. Cloning from `prev` guarantees
        // each update sees the committed result of the previous one.
        setEditedSchema((prev) => {
            const targetSchema = cloneDeep(prev);
            return applyChangeSet(targetSchema, changeSet);
        });
    };

    const setDescription = (value) => set(editedSchema, 'description', value);
    const setFooter = (value) => set(editedSchema, 'x-footer', value);
    const triggerEdit = () => {
        dispatch({type: 'activate', message: `changes to schema ${rootPath} ${get(editedSchema, 'title')}`});
    };

    // Default-open rule: text content (description / x-footer) opens
    // when populated, stays closed when empty so heavy schemas land
    // user attention straight on Properties. Properties is always open
    // — it's the focal surface — but remains user-collapsible for
    // description-only review reads.
    const defaultActiveKey = useMemo(() => {
        if (!editedSchema) return ['properties'];
        const keys = ['properties'];
        if (editedSchema.description) keys.unshift('description');
        if (editedSchema['x-footer']) keys.push('footer');
        return keys;
    }, [editedSchema]);

    return editedSchema ? (
        <Block name={`schema ${editedSchema.key}`} className={className} onCancel={handleCancel} onSave={handleSave} onEdit={triggerEdit} mobile={mobile} disableEdit={disableEdit}>
            {header}
            <Collapse
                className="schemaSections"
                bordered={false}
                defaultActiveKey={defaultActiveKey}
                items={[
                    {
                        key: 'description',
                        label: 'Description',
                        children: (
                            <MarkdownBlock
                                source={schema.description}
                                size={500}
                                placeholder="Description"
                                onValue={setDescription}
                                delegatedEditMode={state.editMode}
                                imageRootPath={rootPath}
                            />
                        ),
                    },
                    {
                        key: 'properties',
                        label: `Properties${propertyCount > 0 ? ` (${propertyCount})` : ''}`,
                        children: (
                            <MasterSchemaView
                                schema={editedSchema}
                                type={editedSchema.type}
                                onChange={handleChangeSet}
                                editMode={state.editMode}
                            />
                        ),
                    },
                    {
                        key: 'footer',
                        label: 'Footer',
                        children: (
                            <MarkdownBlock
                                source={editedSchema['x-footer']}
                                size={200}
                                placeholder="x-footer"
                                onValue={setFooter}
                                delegatedEditMode={state.editMode}
                                imageRootPath={rootPath}
                            />
                        ),
                    },
                ]}
            />
        </Block>
    ) : null;
}
